import type { CallCategory, Partner } from "@expert/shared-types";

import { getAppConfigSingleton } from "@expert/app-config";
import { EligibilityStatus, useProductOfferStore } from "@expert/sales-tools";
import { useMemo } from "react";

import { useCallCategoryStore } from "./callCategoryStore";

export interface PacingTargetResult {
    enabled: boolean;
    targetSeconds: number;
    salesEligibleBufferSeconds: number;
    callCategory: CallCategory | undefined;
    salesEligible: boolean;
    approachingThreshold: number;
    exceededThreshold: number;
}

export function usePacingTarget(partner: Partner, sessionId: string): PacingTargetResult {
    const callCategory = useCallCategoryStore((s) => s.callCategory);

    const pacingConfig = useMemo(() => {
        const partnerConfig = getAppConfigSingleton().getPartnersConfig(partner);
        return partnerConfig.pacingTargets;
    }, [partner]);

    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const salesEligible = products.some((p) => p.status === EligibilityStatus.Eligible);

    const result = useMemo((): PacingTargetResult => {
        if (!pacingConfig?.enabled) {
            return {
                enabled: false,
                targetSeconds: 0,
                salesEligibleBufferSeconds: 0,
                callCategory,
                salesEligible,
                approachingThreshold: 0.8,
                exceededThreshold: 1.0,
            };
        }

        const categoryTarget = callCategory ? pacingConfig.callTypeTargets[callCategory] : undefined;
        const baseTarget = categoryTarget ?? pacingConfig.defaultTargetSeconds;
        const targetSeconds = baseTarget + (salesEligible ? pacingConfig.salesEligibleBufferSeconds : 0);

        return {
            enabled: true,
            targetSeconds,
            salesEligibleBufferSeconds: pacingConfig.salesEligibleBufferSeconds,
            callCategory,
            salesEligible,
            approachingThreshold: pacingConfig.approachingThreshold ?? 0.8,
            exceededThreshold: pacingConfig.exceededThreshold ?? 1.0,
        };
    }, [pacingConfig, callCategory, salesEligible]);

    return result;
}
